/**
 * JacksonSuiteLanding
 *
 * Wave 12.c — DATA-DRIVEN public-safe landing door.
 *
 * Path:       /suite/jackson
 * Fetches:    GET /api/suite-landing/jackson
 * Editable:   Antonia controls every visible field via the
 *             trips.landing_page object (advisor UI in JourneyDetail).
 *
 * Veil-aware: the endpoint already filters destinations by
 * `destination_segments[].veiled` + `reveal_destination`. The component
 * just renders what comes back. If the server returns a
 * `secret_chapter_label`, we surface it alongside the revealed
 * destinations as the gentle teaser line.
 *
 * Strict pre-auth surface: hero imagery, overline, title, welcome copy,
 * date window, traveler label, destination chips, single CTA. Nothing else.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function JacksonSuiteLanding() {
  const [landing, setLanding] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/suite-landing/jackson`)
      .then((r) => {
        if (!cancelled) setLanding(r.data);
      })
      .catch(() => {
        if (!cancelled) setErr("This door isn't open right now.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div
        className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex items-center justify-center px-6"
        data-testid="jackson-suite-landing-error"
      >
        <p className="font-accent italic text-[15px] text-[#8a7a5f]">
          {err}
        </p>
      </div>
    );
  }

  if (!landing) {
    return (
      <div
        className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex items-center justify-center"
        data-testid="jackson-suite-landing-loading"
      >
        <Loader2 className="size-5 animate-spin text-[#c9a96e]" strokeWidth={1.5} />
      </div>
    );
  }

  const destinationLine = (() => {
    const real = (landing.destinations || []).map((d) => d.name);
    const teaser = landing.secret_chapter_label;
    const all = [...real];
    if (teaser) all.push(teaser);
    if (all.length === 0) return teaser || "—";
    return all.join(" · ");
  })();

  // Title may carry an embedded "\n" — render as two lines with a break.
  const titleLines = (landing.title || "Welcome").split("\n");

  return (
    <div
      className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex flex-col"
      data-testid="jackson-suite-landing"
    >
      <header className="px-6 sm:px-12 pt-10 pb-6 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#c9a96e]">
          VÉRAULT · Travels
        </p>
        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#3a342a] hidden sm:block">
          Private · Member-only
        </p>
      </header>

      {/* Editorial hero — 1 or 2 images depending on what's set */}
      <section className="relative px-6 sm:px-12">
        <div
          className={`grid grid-cols-1 ${landing.hero_image_secondary ? "sm:grid-cols-5" : ""} gap-3 sm:gap-4 max-w-[1100px] mx-auto`}
        >
          {landing.hero_image_primary && (
            <div
              className={`${landing.hero_image_secondary ? "sm:col-span-3" : ""} aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-md border border-[#1f1c17]`}
            >
              <img
                src={landing.hero_image_primary}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="jackson-landing-hero-primary"
              />
            </div>
          )}
          {landing.hero_image_secondary && (
            <div className="sm:col-span-2 aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-md border border-[#1f1c17]">
              <img
                src={landing.hero_image_secondary}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="jackson-landing-hero-secondary"
              />
            </div>
          )}
        </div>
      </section>

      <section className="px-6 sm:px-12 pt-16 sm:pt-20 pb-6 max-w-[1100px] mx-auto w-full">
        {landing.overline && (
          <p
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a7a5f] mb-4"
            data-testid="jackson-landing-overline"
          >
            {landing.overline}
          </p>
        )}
        <h1
          className="font-display text-[42px] sm:text-[64px] leading-[1.02] tracking-[-0.01em] text-[#f5f1ea] max-w-[22ch]"
          data-testid="jackson-landing-title"
        >
          {titleLines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < titleLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h1>

        <div
          className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-y-3 sm:gap-x-10 max-w-[760px]"
          data-testid="jackson-landing-meta"
        >
          <div>
            <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#5a5346] mb-1">
              The Journey
            </p>
            <p
              className="font-serif text-[18px] text-[#f5f1ea]"
              data-testid="jackson-landing-destinations"
            >
              {destinationLine}
            </p>
          </div>
          {landing.dates && (
            <div>
              <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#5a5346] mb-1">
                The Window
              </p>
              <p
                className="font-serif text-[18px] text-[#f5f1ea]"
                data-testid="jackson-landing-dates"
              >
                {landing.dates}
              </p>
            </div>
          )}
          {landing.travelers && (
            <div>
              <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#5a5346] mb-1">
                The Travellers
              </p>
              <p
                className="font-serif text-[18px] text-[#f5f1ea]"
                data-testid="jackson-landing-travelers"
              >
                {landing.travelers}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Welcome copy — italic, body-sized, two paragraphs max */}
      <section className="px-6 sm:px-12 py-12 sm:py-16 max-w-[760px] mx-auto w-full">
        {landing.welcome_copy && (
          <p
            className="font-accent italic text-[18px] sm:text-[20px] leading-[1.55] text-[#b7a98a]"
            data-testid="jackson-landing-welcome"
          >
            {landing.welcome_copy}
          </p>
        )}
        {landing.welcome_copy_second && (
          <p
            className="mt-6 font-accent italic text-[15.5px] leading-relaxed text-[#8a7a5f]"
            data-testid="jackson-landing-welcome-second"
          >
            {landing.welcome_copy_second}
          </p>
        )}
      </section>

      <section className="px-6 sm:px-12 pb-20 sm:pb-28 max-w-[760px] mx-auto w-full">
        <Link
          to={landing.access_path || "/suite/jackson/access"}
          className="group inline-flex items-center gap-3 px-7 py-4 bg-[#c9a96e] text-[#0d0c0a] rounded-md hover:bg-[#d9b87e] transition-colors font-mono text-[12px] uppercase tracking-[0.22em]"
          data-testid="jackson-landing-enter-suite"
        >
          Enter Your Suite
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-1"
            strokeWidth={1.5}
          />
        </Link>
        <p className="mt-5 font-accent italic text-[12.5px] text-[#5a5346] leading-relaxed">
          Sign in with the email and access code from Antonia&apos;s note.
        </p>
      </section>

      <footer className="mt-auto px-6 sm:px-12 pb-10 border-t border-[#1f1c17] pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#3a342a]">
          VÉRAULT Travels &middot; Member Suite
        </p>
        <p className="font-accent italic text-[12px] text-[#3a342a]">
          Designed quietly. Held privately.
        </p>
      </footer>
    </div>
  );
}
